import React, { useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { I18N, EBModal, I18NContext } from 'commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Collapse, Button } from '@eb/antd';
import QuietTimeForm from './QuietTimeForm';

const QuietTimeTag = ({ pathId, id, quietTime, timeZone, onDeleteTag, handleQuietTimeSubmitForEdit, isHome }) => {
  const i18n = useContext(I18NContext);
  const { Panel } = Collapse;
  const from = moment({ hour: quietTime.fromHour, minute: quietTime.fromMin });
  const to = moment({ hour: quietTime.toHour, minute: quietTime.toMin });
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent);

  const handleDeleteTag = onDeleteTag ? () => {
    onDeleteTag(id, quietTime);
  } : null;

  const processTime = (time) => {
    const timeString = time.locale('en_us').format('hh:mm')
    if (time.hour() < 12) {
      return i18n.get('contact.path.device.time.frame.quiet.time.am', timeString)
    } else {
      return i18n.get('contact.path.device.time.frame.quiet.time.pm', timeString)
    }
  }

  const useDeleteConfirmModal = (onConfirm, onCancel) => {
    const [isModalOpen, updateIsModalOpen] = useState(false);
    return [(
      <EBModal
        className="change-password-modal"
        type="confirm"
        isOpen={isModalOpen}
        toggleModal={() => {
          updateIsModalOpen(false);
        }}
        onConfirm={() => {
          if (onConfirm) {
            onConfirm();
          }
        }}
        showFooter
      >
        <div className="form-group">
          <I18N>ssp.delete.confirm</I18N>
        </div>
      </EBModal>
    ), updateIsModalOpen];
  };

  const timeRange = `${processTime(from)} - ${processTime(to)}`;
  const [deleteModal, toggleDeleteModal] = useDeleteConfirmModal(handleDeleteTag);

  const [activeQuietTime, setActiveQuietTime] = useState([]);
  const i18nPrefix = 'component.weekly.selector';
  const i18nDays = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const selectDays = [];
  quietTime.days.forEach(day => {
    selectDays.push(i18n[`${i18nPrefix}.${i18nDays[day - 1]}`]);
  });
  const enable24Hours = `${quietTime.fromHour}${quietTime.fromMin}` === `${quietTime.toHour}${quietTime.toMin}`;

  const panelHeader = useRef(null)
  const getPanelHeader = () => (
    <span tabIndex={-1} ref={panelHeader}>
      {`${quietTime.name} (${selectDays.join(', ')}${enable24Hours ? '' : ` ${timeRange}`})`}
    </span>
  )
  const focusOnHeader = () => {
    if (panelHeader) {
      panelHeader.current.focus()
    }
  }

  const getCollapseIcon = (isActive) => (
    <span>
      <FontAwesomeIcon
        icon={isActive ? faChevronDown : faChevronRight}
        width="0.875em"
        height="1em"
      />
    </span>
  );

  if (isHome) {
    return (
      <p className='margin5-B'>
        {`${quietTime.name} (${selectDays.join(', ')}${enable24Hours ? '' : ` ${timeRange}`})`}
      </p>
    )
  }

  return (
    <div className="quiet-time-collapse-block">
      {handleDeleteTag && deleteModal}
      <Button
        className="p-0 h-auto quiet-time-delete-button"
        type="link"
        aria-label={`${quietTime.name} ${i18n['ssp.address.grid.delete']}`}
        onClick={event => {
          event.stopPropagation();
          if (handleDeleteTag) {
            toggleDeleteModal(true);
          }
        }}
        onKeyPress={event => {
          event.stopPropagation();
          if (event.key === 'Enter') {
            if (handleDeleteTag) {
              toggleDeleteModal(true);
            }
          }
        }}
      >
        <FontAwesomeIcon
          icon={faTrashAlt}
          alt={`${quietTime.name} ${i18n['ssp.address.grid.delete']}`}
          width="0.875em"
          height="1em"
        />
      </Button>
      <Collapse
        activeKey={activeQuietTime}
        destroyInactivePanel
        onChange={(key) => { setActiveQuietTime(key); }}
        className="rounded-0"
        expandIcon={({ isActive }) => getCollapseIcon(isActive)}
      >
        <Panel
          header={getPanelHeader()}
          key={`tag-${pathId}-${id}`}
          className="rounded-0"
        >
          <QuietTimeForm
            quietTimeFrame={quietTime}
            timeZone={timeZone}
            quietTimeId={`time-${pathId}-${id}`}
            onClickCancel={() => { setActiveQuietTime([]); focusOnHeader(); }}
            onSubmit={(updatedQuietTime) => {
              handleQuietTimeSubmitForEdit(id, updatedQuietTime, () => { setActiveQuietTime([]); focusOnHeader(); });
            }}
          />
        </Panel>
      </Collapse>
    </div>
  );
};

QuietTimeTag.propTypes = {
  pathId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  id: PropTypes.number.isRequired,
  timeZone: PropTypes.string.isRequired,
  quietTime: PropTypes.shape().isRequired,
  onDeleteTag: PropTypes.func,
  handleQuietTimeSubmitForEdit: PropTypes.func.isRequired,
  isHome: PropTypes.bool
};

QuietTimeTag.defaultProps = {
  onDeleteTag: null,
  isHome: false
};

export default QuietTimeTag;
